import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/client";

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

const ACTION_LABELS = {
  inspection_created: "Created inspection",
  answers_submitted: "Submitted answers",
  finding_accepted: "Accepted finding",
  finding_declined: "Declined finding",
  evidence_uploaded: "Uploaded evidence",
  inspection_completed: "Completed inspection",
  report_generated: "Generated report",
  report_sent: "Sent report",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-NL", {
    day: "2-digit", month: "short", year: "numeric",
  }) + " " + d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ borderRadius: 12, border: "1px solid #d0dcea", padding: 16, background: "white" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6b8cae", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color }}>{value ?? 0}</div>
    </div>
  );
}

export default function Admin() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("activity");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    api("/admin/dashboard", { token })
      .then(setData)
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [token, user, navigate]);

  if (loading) return <div style={{ padding: 24, color: "#6b8cae", fontSize: 14 }}>Loading...</div>;
  if (error) return <div style={{ padding: 24, color: "#c0392b", fontSize: 14 }}>{error}</div>;
  if (!data) return null;

  const stats = data.stats || {};
  const totalInspections = Object.values(stats).reduce((a, b) => a + b, 0);

  const thStyle = {
    textAlign: "left", padding: "12px 16px",
    fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6b8cae",
  };
  const tdStyle = { padding: "12px 16px" };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", paddingTop: 24, paddingBottom: 24, display: "flex", flexDirection: "column", gap: 24 }}>

      <div>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{ fontSize: 12, color: "#6b8cae", background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          ← Back to home
        </button>
      </div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e3a5f", margin: 0 }}>Admin Dashboard</h1>

      {/* Stats row */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr",
        gap: 16,
      }}>
        <StatCard label="Total users" value={data.users.length} color="#1e3a5f" />
        <StatCard label="Total inspections" value={totalInspections} color="#1e3a5f" />
        <StatCard label="In progress" value={stats["in_progress"]} color="#92610a" />
        <StatCard label="Completed" value={stats["completed"]} color="#2e7d32" />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, borderBottom: "1px solid #d0dcea" }}>
        {["activity", "users"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "8px 16px", fontSize: 14, fontWeight: 500,
              background: "none", border: "none", cursor: "pointer",
              borderBottom: tab === t ? "2px solid #1e3a5f" : "2px solid transparent",
              color: tab === t ? "#1e3a5f" : "#6b8cae",
              marginBottom: -1,
            }}
          >
            {t === "activity" ? "Recent activity" : "Users"}
          </button>
        ))}
      </div>

      {/* Activity tab */}
      {tab === "activity" && (
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}>
            <thead style={{ background: "#f8fafc" }}>
              <tr>
                <th style={thStyle}>When</th>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Inspection / Vessel</th>
              </tr>
            </thead>
            <tbody>
              {data.activity.length === 0 && (
                <tr><td colSpan={4} style={{ ...tdStyle, textAlign: "center", color: "#6b8cae" }}>No activity yet</td></tr>
              )}
              {data.activity.map((row, idx) => (
                <tr key={row.id} style={{ borderTop: idx === 0 ? "1px solid #d0dcea" : "1px solid #edf2f7" }}>
                  <td style={{ ...tdStyle, color: "#6b8cae", whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</td>
                  <td style={{ ...tdStyle, color: "#3a5068" }}>{row.user_email}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 20,
                      fontSize: 12, background: "#e8eef5", color: "#1e3a5f", fontWeight: 500,
                    }}>
                      {ACTION_LABELS[row.action] || row.action}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: "#6b8cae" }}>
                    {row.vessel_name !== "—" && (
                      <span style={{ fontWeight: 500 }}>{row.vessel_name} — </span>
                    )}
                    {row.inspection_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Users tab */}
      {tab === "users" && (
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}>
            <thead style={{ background: "#f8fafc" }}>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.users.length === 0 && (
                <tr><td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: "#6b8cae" }}>No users yet</td></tr>
              )}
              {data.users.map((u, idx) => (
                <tr key={u.id} style={{ borderTop: idx === 0 ? "1px solid #d0dcea" : "1px solid #edf2f7" }}>
                  <td style={{ ...tdStyle, fontWeight: 500, color: "#1e3a5f" }}>
                    {u.first_name || u.last_name
                      ? `${u.first_name} ${u.last_name}`.trim()
                      : "—"}
                    {u.role === "admin" && (
                      <span style={{
                        marginLeft: 8, fontSize: 12, background: "#ede9fe",
                        color: "#5b21b6", padding: "2px 6px", borderRadius: 4,
                      }}>admin</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: "#6b8cae" }}>{u.email}</td>
                  <td style={{ ...tdStyle, color: "#6b8cae" }}>{u.company_name}</td>
                  <td style={{ ...tdStyle, color: "#6b8cae" }}>{u.role_in_company}</td>
                  <td style={{ ...tdStyle, color: "#6b8cae", whiteSpace: "nowrap" }}>{formatDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
